// Pure formatting helpers for the Upcoming Schedule view. Kept separate from the
// components so they can be unit-tested without rendering.
import { RESOURCE_TIMEZONE, getLocalTimezoneLabel, getResourceTimezoneLabel } from '../../../utils/datetime';

// Shared card chrome for the schedule panels (padding is applied per panel).
export const CARD_BASE =
  'bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm';

export const USE_CASE_LABELS = {
  wind_power: 'Wind power',
  solar_power: 'Solar power',
  wind_power_ramp: 'Wind ramp',
  mfrr_price: 'mFRR Price',
  mfrr_energy: 'mFRR Energy',
};

export const formatUseCase = (useCase) => {
  if (!useCase) return '';
  return (
    USE_CASE_LABELS[useCase] ||
    useCase.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

const pad2 = (n) => String(n).padStart(2, '0');

export const gateHoursLabel = (hours = []) => {
  if (!hours || hours.length === 0) return '';
  if (hours.length >= 24) return 'every hour';
  return hours.map((h) => `${pad2(h)}:00`).join(', ');
};

// "Every hour" for intraday; "Daily · opens 09:00 → gate 10:00 CEST" for
// day-ahead gates (open hours come from the backend so the open/gate offset
// stays a single source of truth).
export const cadenceLabel = (pattern, at = new Date()) => {
  if (pattern.cadence === 'hourly') return 'Every hour';
  const tz = getResourceTimezoneLabel(RESOURCE_TIMEZONE, at);
  const gates = pattern.gate_hours_cet || [];
  const opens = pattern.open_hours_cet || [];
  const windows = gates.map((gateHour, i) =>
    opens[i] != null
      ? `opens ${pad2(opens[i])}:00 → gate ${pad2(gateHour)}:00`
      : `gate ${pad2(gateHour)}:00`
  );
  return `Daily · ${windows.join(' · ')} ${tz}`;
};

// "in 14m" / "in 2h 05m" / "in 3d 4h". Minute granularity is enough here.
export const formatCountdown = (targetIso, now = Date.now()) => {
  const ms = new Date(targetIso).getTime() - now;
  if (Number.isNaN(ms)) return '';
  if (ms <= 0) return 'now';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${pad2(mins)}m`;
  return `in ${mins}m`;
};

// Openings that share a gate closure are the same session; group them so each
// row is one session listing the horizons (challenges) that open at it.
export const groupBySession = (openings = []) => {
  const map = new Map();
  for (const opening of openings) {
    const key = opening.gate_closure;
    if (!map.has(key)) {
      map.set(key, {
        gateClosure: key,
        sessionOpen: opening.session_open || null,
        items: [],
      });
    }
    map.get(key).items.push(opening);
  }
  const sessions = [...map.values()];
  for (const session of sessions) {
    session.items.sort((a, b) => a.horizon_offset - b.horizon_offset);
  }
  sessions.sort(
    (a, b) => new Date(a.gateClosure).getTime() - new Date(b.gateClosure).getTime()
  );
  return sessions;
};

// 'YYYY-MM-DD' calendar day of an instant in the resource timezone; used to
// group agenda rows by day.
export const resourceDayKey = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: RESOURCE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
};

// "Today · Mon, Aug 11" / "Tomorrow · Tue, Aug 12" / "Wed, Aug 13" for the
// agenda day separators, all in the resource timezone.
export const agendaDayLabel = (iso, now = Date.now()) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: RESOURCE_TIMEZONE,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const dayKey = resourceDayKey(iso);
  const todayKey = resourceDayKey(new Date(now).toISOString());
  const tomorrowKey = resourceDayKey(new Date(now + 86400000).toISOString());
  const base = fmt.format(d);
  if (dayKey === todayKey) return `Today · ${base}`;
  if (dayKey === tomorrowKey) return `Tomorrow · ${base}`;
  return base;
};

// "10:30" in the resource timezone, for agenda row times.
export const resourceHourMinute = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: RESOURCE_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
};

const weekdayHourMinute = (iso, tz) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const p = Object.fromEntries(
    fmt.formatToParts(d).map(({ type, value }) => [type, value])
  );
  return `${p.weekday} ${p.hour}:${p.minute}`;
};

const localTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const formatGateResourceTz = (iso) => weekdayHourMinute(iso, RESOURCE_TIMEZONE);
export const formatGateLocalTz = (iso) => weekdayHourMinute(iso, localTz());

export const resourceTzLabel = (iso) =>
  getResourceTimezoneLabel(RESOURCE_TIMEZONE, new Date(iso));
export const localTzLabel = (iso) => getLocalTimezoneLabel(iso);
